import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  formatFinancialYearLabel,
  getCalendarMonthFromFySlot,
  getFinancialYearDropdownOptions,
  FY_MONTH_ORDER_SHORT,
} from "../utils/financialYear";
import { readMonthlySaleReportParams, monthlySaleReportQuery } from "../utils/monthlySaleReportNav";
import { APP_BRANCHES } from "../config/appBranches";
import { loadExpenseRowsForFinancialYear } from "../features/expenseDataImport/services/expenseReportService";
import { MONTH_LABELS } from "../features/expenseDataImport/services/expenseReportService";
import {
  TRANSFER_RULES,
  buildBranchWiseTransferReport,
  buildFyTransferMonthSummary,
  buildMonthBranchTransferReport,
  buildPendingSettlementReport,
  createSettlementJournal,
  loadSettlementJournal,
  loadSettlementJournalsForYear,
} from "../features/expenseDataImport/services/expenseBranchTransferService";
import { branchIdFromAppBranch } from "../features/expenseDataImport/services/expenseReportService";
import { winsBranchSDebtorOpeningBalance } from "../features/expenseDataImport/constants";
import "./MonthlySaleReportPage.css";
import "./ExpenseBranchTransferPage.css";

const MONTH_OPTIONS = MONTH_LABELS;
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "branch", label: "Branch-wise" },
  { id: "pending", label: "Pending settlement" },
  { id: "journal", label: "Journal history" },
];

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function pendingAmountClass(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.01) return "ebt-net-zero";
  return v > 0 ? "ebt-net-positive" : "ebt-net-negative";
}

function fmtTs(v) {
  if (!v) return "—";
  if (typeof v?.toDate === "function") return v.toDate().toLocaleString("en-IN");
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN");
}

function DetailTable({
  title,
  subtitle,
  rows,
  emptyHint,
  showDebitCredit = false,
  debitLabel = "Debit",
  creditLabel = "Credit",
}) {
  return (
    <section className="ebt-section">
      <div className="ebt-section-head">
        <h2 className="ebt-section-title">{title}</h2>
        {subtitle ? <p className="ebt-section-sub">{subtitle}</p> : null}
      </div>
      {!rows?.length ? (
        <p className="ebt-empty">{emptyHint || "No matching vouchers for this month."}</p>
      ) : (
        <div className="msr-precalc-table-wrap">
          <table className="msr-precalc-table ebt-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Branch</th>
                <th>Voucher type</th>
                <th>Voucher no</th>
                <th>Party</th>
                <th>Txn type</th>
                {showDebitCredit ? (
                  <>
                    <th className="msr-num">{debitLabel}</th>
                    <th className="msr-num">{creditLabel}</th>
                  </>
                ) : (
                  <th className="msr-num">Amount</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.voucherNo}-${r.tranDate}-${i}`}>
                  <td>{r.tranDate}</td>
                  <td>{r.branchLabel}</td>
                  <td>{r.voucherTypeName || "—"}</td>
                  <td>{r.voucherNo}</td>
                  <td className="ebt-party-fixed">{r.partyName || "—"}</td>
                  <td>{r.transactionType || "—"}</td>
                  {showDebitCredit ? (
                    <>
                      <td className="msr-num">{r.debit > 0 ? fmt(r.debit) : "—"}</td>
                      <td className="msr-num">{r.credit > 0 ? fmt(r.credit) : "—"}</td>
                    </>
                  ) : (
                    <td className="msr-num">{fmt(r.amountAbs)}</td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="text-right font-semibold">
                  Section total
                </td>
                {showDebitCredit ? (
                  <>
                    <td className="msr-num font-semibold">
                      {fmt(rows.reduce((s, r) => s + (r.debit || 0), 0))}
                    </td>
                    <td className="msr-num font-semibold">
                      {fmt(rows.reduce((s, r) => s + (r.credit || 0), 0))}
                    </td>
                  </>
                ) : (
                  <td className="msr-num font-semibold">
                    {fmt(rows.reduce((s, r) => s + (r.amountAbs || 0), 0))}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

export default function ExpenseTransferMonthlyReportPage() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const base = readMonthlySaleReportParams(searchParams, now);
  const branchParam = searchParams.get("branch");
  const initial = {
    ...base,
    branchFilter: branchParam === "all" || !branchParam ? "all" : base.branchFilter,
  };

  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [fySlot, setFySlot] = useState(initial.fySlot);
  const [branchFilter, setBranchFilter] = useState(initial.branchFilter);
  const [activeTab, setActiveTab] = useState("overview");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState("");
  const [journal, setJournal] = useState(null);
  const [journalHistory, setJournalHistory] = useState([]);
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalNote, setJournalNote] = useState("");

  const { calendarYear: year, monthIndex } = getCalendarMonthFromFySlot(fyStartYear, fySlot);
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const periodLabel = `${MONTH_OPTIONS[monthIndex]} ${year}`;
  const viewBranchId = branchFilter === "all" ? "all" : branchIdFromAppBranch(branchFilter);

  useEffect(() => {
    const q = new URLSearchParams(monthlySaleReportQuery(fyStartYear, fySlot, branchFilter === "all" ? "Main" : branchFilter));
    if (branchFilter === "all") q.set("branch", "all");
    setSearchParams(q.toString(), { replace: true });
  }, [fyStartYear, fySlot, branchFilter, setSearchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setProgress("Loading expense data…");
    try {
      const data = await loadExpenseRowsForFinancialYear(fyStartYear, (done, total) => {
        setProgress(`Loading month ${done} / ${total}…`);
      });
      setRows(data);
    } catch (e) {
      toast.error(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
      setProgress("");
    }
  }, [fyStartYear]);

  const loadJournals = useCallback(async () => {
    try {
      const [j, history] = await Promise.all([
        loadSettlementJournal(year, monthIndex),
        loadSettlementJournalsForYear([fyStartYear, fyStartYear + 1]),
      ]);
      setJournal(j);
      setJournalHistory(history);
    } catch {
      setJournal(null);
      setJournalHistory([]);
    }
  }, [year, monthIndex, fyStartYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadJournals();
  }, [loadJournals]);

  const monthReport = useMemo(
    () => buildMonthBranchTransferReport(rows, { calendarYear: year, monthIndex, viewBranchId }),
    [rows, year, monthIndex, viewBranchId]
  );

  const fySummary = useMemo(() => buildFyTransferMonthSummary(rows, "all"), [rows]);

  const openingBalance = winsBranchSDebtorOpeningBalance(fyStartYear);

  // Running balance = opening + cumulative (Transfer out + Journal GST − Transfer in − Receipt).
  const ledgerMovement = (m) =>
    m.transferOut + m.collectionsJournalDebit - m.transferIn - m.collectionsReceipt;

  // Running ledger balance per FY slot: opening balance + cumulative ledger movement.
  const runningBalances = useMemo(() => {
    let bal = openingBalance;
    const bySlot = new Map();
    for (let slot = 0; slot < FY_MONTH_ORDER_SHORT.length; slot++) {
      const m = fySummary.months.find((x) => x.fySlot === slot);
      if (m) bal += ledgerMovement(m);
      bySlot.set(slot, bal);
    }
    return bySlot;
  }, [fySummary, openingBalance]);

  const closingBalance =
    openingBalance + fySummary.months.reduce((s, m) => s + ledgerMovement(m), 0);

  const branchWise = useMemo(
    () => buildBranchWiseTransferReport(rows, { calendarYear: year, monthIndex }),
    [rows, year, monthIndex]
  );

  const pendingMonths = useMemo(() => buildPendingSettlementReport(fySummary), [fySummary]);

  const settlementStatus = journal
    ? "Settled"
    : Math.abs(monthReport.summary.pendingBalance) < 0.01
      ? "Balanced"
      : "Pending";

  const handleGenerateJournal = async () => {
    if (journal) {
      toast.error("Journal already posted for this month.");
      return;
    }
    const { pendingBalance, transferOutTotal, transferInTotal, collectionsTotal } = monthReport.summary;
    if (!(Math.abs(pendingBalance) > 0.001)) {
      toast.error("Pending balance is zero — no journal required.");
      return;
    }
    setJournalSaving(true);
    const tId = toast.loading("Posting month-end journal…");
    try {
      let userEmail = "";
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        userEmail = u?.email || u?.name || "";
      } catch {
        /* ignore */
      }
      await createSettlementJournal({
        calendarYear: year,
        monthIndex,
        transferOutTotal,
        transferInTotal,
        collectionsTotal,
        pendingBalance,
        createdBy: userEmail,
        note: journalNote,
      });
      await loadJournals();
      toast.success(`Journal posted for ${periodLabel}. Amount: ${fmt(pendingBalance)}`, { id: tId });
    } catch (e) {
      toast.error(e?.message || String(e), { id: tId });
    } finally {
      setJournalSaving(false);
    }
  };

  return (
    <>
      <div className="msr-no-print">
        <MenuBar />
        <HeaderBar />
      </div>

      <main className="msr-upload-page ebt-page msr-no-print">
        <header className="msr-upload-page-head">
          <div>
            <p className="msr-upload-page-kicker">Reports · Branch finance</p>
            <h1 className="msr-upload-page-title">Expense — branch transfer & payments</h1>
            <p className="msr-upload-page-sub">
              Main ↔ Branch transfers, collection remittances, and month-end journal settlement from uploaded
              expense vouchers.
            </p>
          </div>
          <Link to="/reports/expense-upload" className="msr-upload-page-back">
            Expense upload →
          </Link>
        </header>

        <div className="ebt-rules-grid">
          {TRANSFER_RULES.map((rule) => (
            <div key={rule.id} className="ebt-rule-card">
              <div className="ebt-rule-title">{rule.title}</div>
              <div className="ebt-rule-row">
                <span>Voucher</span>
                <strong>{rule.voucherType}</strong>
              </div>
              <div className="ebt-rule-row">
                <span>Party</span>
                <strong className="ebt-party-fixed">{rule.party}</strong>
              </div>
              <div className="ebt-rule-row">
                <span>Branch</span>
                <span>{rule.branch}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="msr-toolbar msr-upload-toolbar">
          <label className="msr-toolbar-label">
            Financial year{" "}
            <select className="msr-toolbar-select" value={fyStartYear} onChange={(e) => setFyStartYear(Number(e.target.value))}>
              {fySelectOptions.map(({ startYear, label }) => (
                <option key={startYear} value={startYear}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label">
            View branch{" "}
            <select className="msr-toolbar-select" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="all">All branches</option>
              {APP_BRANCHES.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="msr-print-btn" onClick={loadData} disabled={loading}>
            Refresh
          </button>
          <button type="button" className="msr-print-btn" onClick={() => window.print()} disabled={loading}>
            Print A4
          </button>
        </div>

        <nav className="ebt-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`ebt-tab ${activeTab === t.id ? "ebt-tab-active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {loading ? (
          <p className="text-sm text-slate-500 py-6">{progress || "Loading…"}</p>
        ) : (
          <>
            <div className="ebt-dashboard">
              <div className="ebt-card ebt-card-out">
                <span>Total transfer out</span>
                <strong>{fmt(monthReport.summary.transferOutTotal)}</strong>
                <em>{periodLabel}</em>
              </div>
              <div className="ebt-card ebt-card-in">
                <span>Total transfer in</span>
                <strong>{fmt(monthReport.summary.transferInTotal)}</strong>
                <em>Branch → Main</em>
              </div>
              <div className="ebt-card ebt-card-rcpt">
                <span>Total collections</span>
                <strong>{fmt(monthReport.summary.collectionsTotal)}</strong>
                <em>
                  Receipt {fmt(monthReport.summary.collectionsReceiptTotal)} + Journal (collection){" "}
                  {fmt(monthReport.summary.collectionsJournalCreditTotal)}
                </em>
              </div>
              <div className="ebt-card ebt-card-pending">
                <span>Pending balance</span>
                <strong className={pendingAmountClass(monthReport.summary.pendingBalance)}>
                  {fmt(monthReport.summary.pendingBalance)}
                </strong>
                <em>Out + Journal (GST) − In − Receipt</em>
              </div>
              <div className={`ebt-card ebt-card-status ebt-status-${settlementStatus.toLowerCase()}`}>
                <span>Settlement status</span>
                <strong>{settlementStatus}</strong>
                <em>{journal ? `Journal ${journal.monthId}` : periodLabel}</em>
              </div>
            </div>

            {activeTab === "overview" && (
              <>
                <section className="ebt-section">
                  <h2 className="ebt-section-title">Month-wise transfer summary — FY {fyLabel}</h2>
                  <p className="ebt-section-sub">
                    WINS BRANCH (S.DEBTOR) opening balance (FY {fyLabel}): {fmt(openingBalance)} · Click a month
                    row to view its details and settlement below.
                  </p>
                  <p className="ebt-section-sub">
                    <strong>Pending</strong> = Transfer out + Journal (GST) − Transfer in − Receipt ·{" "}
                    <strong>Balance</strong> = Opening + cumulative monthly movement (same formula).
                  </p>
                  <div className="msr-precalc-table-wrap">
                    <table className="msr-precalc-table ebt-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th className="msr-num">Transfer out</th>
                          <th className="msr-num">Journal (GST)</th>
                          <th className="msr-num">Transfer in</th>
                          <th className="msr-num">Journal (collection)</th>
                          <th className="msr-num">Receipt</th>
                          <th className="msr-num">Collections</th>
                          <th className="msr-num">Pending</th>
                          <th className="msr-num">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="font-semibold">Opening balance</td>
                          <td className="msr-num">—</td>
                          <td className="msr-num">—</td>
                          <td className="msr-num">—</td>
                          <td className="msr-num">—</td>
                          <td className="msr-num">—</td>
                          <td className="msr-num">—</td>
                          <td className="msr-num">—</td>
                          <td className="msr-num font-semibold">{fmt(openingBalance)}</td>
                        </tr>
                        {FY_MONTH_ORDER_SHORT.map((label, slot) => {
                          const m = fySummary.months.find((x) => x.fySlot === slot);
                          const active = slot === fySlot;
                          return (
                            <tr
                              key={label}
                              className={active ? "ebt-row-active" : ""}
                              onClick={() => m && setFySlot(slot)}
                              style={m ? { cursor: "pointer" } : undefined}
                              title={m ? "Click to select this month" : undefined}
                            >
                              <td>
                                {label}
                                {m?.calendarYear ? ` ${m.calendarYear}` : ""}
                              </td>
                              <td className="msr-num">{m ? fmt(m.transferOut) : "—"}</td>
                              <td className="msr-num">{m ? fmt(m.collectionsJournalDebit) : "—"}</td>
                              <td className="msr-num">{m ? fmt(m.transferIn) : "—"}</td>
                              <td className="msr-num">{m ? fmt(m.collectionsJournalCredit) : "—"}</td>
                              <td className="msr-num">{m ? fmt(m.collectionsReceipt) : "—"}</td>
                              <td className="msr-num">{m ? fmt(m.collections) : "—"}</td>
                              <td className={`msr-num ${m ? pendingAmountClass(m.pending) : ""}`}>
                                {m ? fmt(m.pending) : "—"}
                              </td>
                              <td className="msr-num">{fmt(runningBalances.get(slot))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="font-semibold">FY total</td>
                          <td className="msr-num font-semibold">{fmt(fySummary.totals.transferOut)}</td>
                          <td className="msr-num font-semibold">{fmt(fySummary.totals.collectionsJournalDebit)}</td>
                          <td className="msr-num font-semibold">{fmt(fySummary.totals.transferIn)}</td>
                          <td className="msr-num font-semibold">{fmt(fySummary.totals.collectionsJournalCredit)}</td>
                          <td className="msr-num font-semibold">{fmt(fySummary.totals.collectionsReceipt)}</td>
                          <td className="msr-num font-semibold">{fmt(fySummary.totals.collections)}</td>
                          <td className={`msr-num font-semibold ${pendingAmountClass(fySummary.totals.pending)}`}>
                            {fmt(fySummary.totals.pending)}
                          </td>
                          <td className="msr-num font-semibold">{fmt(closingBalance)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </section>

                <DetailTable
                  title="Collections — Journal (GST / collection)"
                  subtitle="Journal + Payment · WINS BRANCH (S.DEBTOR) — GST (debit incl. Payment) and collection (credit) in separate columns"
                  rows={monthReport.collectionJournals}
                  emptyHint="No Journal collection vouchers this month."
                  showDebitCredit
                  debitLabel="Journal (GST)"
                  creditLabel="Journal (collection)"
                />

                <section className="ebt-section ebt-journal-panel">
                  <h2 className="ebt-section-title">Monthly settlement journal</h2>
                  <p className="ebt-section-sub">
                    Month-end adjustment for Main Branch Transfer Out. Journal amount = pending balance (
                    {fmt(monthReport.summary.pendingBalance)}).
                  </p>

                  <div className="ebt-journal-calc">
                    <div>
                      <span>Transfer out</span>
                      <strong>{fmt(monthReport.summary.transferOutTotal)}</strong>
                    </div>
                    <div className="ebt-journal-op">−</div>
                    <div>
                      <span>Transfer in</span>
                      <strong>{fmt(monthReport.summary.transferInTotal)}</strong>
                    </div>
                    <div className="ebt-journal-op">−</div>
                    <div>
                      <span>Collections</span>
                      <strong>{fmt(monthReport.summary.collectionsTotal)}</strong>
                      <div className="text-xs text-slate-500 font-normal mt-1">
                        Receipt {fmt(monthReport.summary.collectionsReceiptTotal)} + Journal (collection){" "}
                        {fmt(monthReport.summary.collectionsJournalCreditTotal)}
                      </div>
                    </div>
                    <div className="ebt-journal-op">=</div>
                    <div className="ebt-journal-result">
                      <span>Journal amount</span>
                      <strong>{fmt(journal?.journalAmount ?? monthReport.summary.pendingBalance)}</strong>
                    </div>
                  </div>

                  {journal ? (
                    <div className="ebt-audit">
                      <div className="ebt-audit-badge">Posted</div>
                      <dl className="ebt-audit-grid">
                        <div>
                          <dt>Created by</dt>
                          <dd>{journal.createdBy || "—"}</dd>
                        </div>
                        <div>
                          <dt>Created date</dt>
                          <dd>{fmtTs(journal.createdAt)}</dd>
                        </div>
                        <div>
                          <dt>Last modified by</dt>
                          <dd>{journal.lastModifiedBy || journal.createdBy || "—"}</dd>
                        </div>
                        <div>
                          <dt>Last modified date</dt>
                          <dd>{fmtTs(journal.lastModifiedAt || journal.createdAt)}</dd>
                        </div>
                        <div>
                          <dt>Amount</dt>
                          <dd>{fmt(journal.journalAmount)}</dd>
                        </div>
                        <div>
                          <dt>Purpose</dt>
                          <dd>{journal.purpose || "—"}</dd>
                        </div>
                      </dl>
                      {journal.note ? <p className="ebt-note">Note: {journal.note}</p> : null}
                    </div>
                  ) : (
                    <div className="ebt-journal-actions">
                      <label className="ebt-note-input">
                        Note (optional)
                        <input
                          type="text"
                          value={journalNote}
                          onChange={(e) => setJournalNote(e.target.value)}
                          placeholder="Settlement reference…"
                          className="msr-toolbar-select w-full max-w-md mt-1"
                        />
                      </label>
                      <button
                        type="button"
                        className="msr-print-btn"
                        disabled={journalSaving || Math.abs(monthReport.summary.pendingBalance) < 0.001}
                        onClick={handleGenerateJournal}
                      >
                        {journalSaving ? "Posting…" : "Generate month-end journal"}
                      </button>
                      <p className="text-xs text-slate-500">
                        Duplicate journals for the same month are blocked. Party is fixed to WINS BRANCH (S.DEBTOR).
                      </p>
                    </div>
                  )}
                </section>
              </>
            )}

            {activeTab === "branch" && (
              <section className="ebt-section">
                <h2 className="ebt-section-title">Branch-wise transfer report — {periodLabel}</h2>
                <div className="msr-precalc-table-wrap">
                  <table className="msr-precalc-table ebt-table">
                    <thead>
                      <tr>
                        <th>Branch</th>
                        <th className="msr-num">Transfer out</th>
                        <th className="msr-num">Transfer in</th>
                        <th className="msr-num">Receipt</th>
                        <th className="msr-num">Journal (GST)</th>
                        <th className="msr-num">Journal (collection)</th>
                        <th className="msr-num">Collections</th>
                        <th className="msr-num">Pending</th>
                        <th className="msr-num">Lines</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branchWise.map((b) => (
                        <tr key={b.branchId}>
                          <td>{b.branchLabel}</td>
                          <td className="msr-num">{fmt(b.transferOut)}</td>
                          <td className="msr-num">{fmt(b.transferIn)}</td>
                          <td className="msr-num">{fmt(b.collectionsReceipt)}</td>
                          <td className="msr-num">{fmt(b.collectionsJournalDebit)}</td>
                          <td className="msr-num">{fmt(b.collectionsJournalCredit)}</td>
                          <td className="msr-num">{fmt(b.collections)}</td>
                          <td className={`msr-num ${pendingAmountClass(b.pending)}`}>
                            {fmt(b.pending)}
                          </td>
                          <td className="msr-num">{b.lines}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {activeTab === "pending" && (
              <section className="ebt-section">
                <h2 className="ebt-section-title">Pending settlement report — FY {fyLabel}</h2>
                <p className="ebt-section-sub">
                  Months where transfer out + journal (GST) ≠ transfer in + receipt.
                </p>
                {!pendingMonths.length ? (
                  <p className="ebt-empty">No pending settlement months in this financial year.</p>
                ) : (
                  <div className="msr-precalc-table-wrap">
                    <table className="msr-precalc-table ebt-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th className="msr-num">Transfer out</th>
                          <th className="msr-num">Transfer in</th>
                          <th className="msr-num">Collections</th>
                          <th className="msr-num">Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingMonths.map((m) => (
                          <tr key={m.fySlot}>
                            <td>
                              {m.fyMonthLabel} {m.calendarYear}
                            </td>
                            <td className="msr-num">{fmt(m.transferOut)}</td>
                            <td className="msr-num">{fmt(m.transferIn)}</td>
                            <td className="msr-num">{fmt(m.collections)}</td>
                            <td className={`msr-num ${pendingAmountClass(m.pending)}`}>{fmt(m.pending)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {activeTab === "journal" && (
              <section className="ebt-section">
                <h2 className="ebt-section-title">Journal history</h2>
                {!journalHistory.length ? (
                  <p className="ebt-empty">No settlement journals posted yet.</p>
                ) : (
                  <div className="msr-precalc-table-wrap">
                    <table className="msr-precalc-table ebt-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th className="msr-num">Journal amount</th>
                          <th className="msr-num">Transfer out</th>
                          <th className="msr-num">Transfer in</th>
                          <th className="msr-num">Collections</th>
                          <th>Created by</th>
                          <th>Created</th>
                          <th>Modified by</th>
                          <th>Modified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {journalHistory.map((j) => (
                          <tr key={j.id}>
                            <td>{j.monthId}</td>
                            <td className="msr-num">{fmt(j.journalAmount)}</td>
                            <td className="msr-num">{fmt(j.transferOutTotal)}</td>
                            <td className="msr-num">{fmt(j.transferInTotal)}</td>
                            <td className="msr-num">{fmt(j.collectionsTotal)}</td>
                            <td>{j.createdBy || "—"}</td>
                            <td>{fmtTs(j.createdAt)}</td>
                            <td>{j.lastModifiedBy || "—"}</td>
                            <td>{fmtTs(j.lastModifiedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </main>

      <section className="ebt-print-sheet">
        <header className="ebt-print-head">
          <div className="ebt-print-company">WINS AUTOS</div>
          <h1 className="ebt-print-title">Branch Transfer &amp; Payments — Month-wise Summary</h1>
          <div className="ebt-print-meta">
            <span>
              Financial year: <strong>{fyLabel}</strong>
            </span>
            <span>
              Party: <strong>WINS BRANCH (S.DEBTOR)</strong>
            </span>
            <span>
              Opening balance: <strong>{fmt(openingBalance)}</strong>
            </span>
            <span>Printed: {new Date().toLocaleString("en-IN")}</span>
          </div>
        </header>

        <table className="ebt-print-table">
          <thead>
            <tr>
              <th>Month</th>
              <th className="msr-num">Transfer out</th>
              <th className="msr-num">Journal (GST)</th>
              <th className="msr-num">Transfer in</th>
              <th className="msr-num">Journal (collection)</th>
              <th className="msr-num">Receipt</th>
              <th className="msr-num">Collections</th>
              <th className="msr-num">Pending</th>
              <th className="msr-num">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="ebt-print-opening">
              <td>Opening balance</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">—</td>
              <td className="msr-num">{fmt(openingBalance)}</td>
            </tr>
            {FY_MONTH_ORDER_SHORT.map((label, slot) => {
              const m = fySummary.months.find((x) => x.fySlot === slot);
              return (
                <tr key={label}>
                  <td>
                    {label}
                    {m?.calendarYear ? ` ${m.calendarYear}` : ""}
                  </td>
                  <td className="msr-num">{m ? fmt(m.transferOut) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.collectionsJournalDebit) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.transferIn) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.collectionsJournalCredit) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.collectionsReceipt) : "—"}</td>
                  <td className="msr-num">{m ? fmt(m.collections) : "—"}</td>
                  <td className={`msr-num ${m ? pendingAmountClass(m.pending) : ""}`}>
                    {m ? fmt(m.pending) : "—"}
                  </td>
                  <td className="msr-num">{fmt(runningBalances.get(slot))}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>FY total / Closing</td>
              <td className="msr-num">{fmt(fySummary.totals.transferOut)}</td>
              <td className="msr-num">{fmt(fySummary.totals.collectionsJournalDebit)}</td>
              <td className="msr-num">{fmt(fySummary.totals.transferIn)}</td>
              <td className="msr-num">{fmt(fySummary.totals.collectionsJournalCredit)}</td>
              <td className="msr-num">{fmt(fySummary.totals.collectionsReceipt)}</td>
              <td className="msr-num">{fmt(fySummary.totals.collections)}</td>
              <td className={`msr-num ${pendingAmountClass(fySummary.totals.pending)}`}>
                {fmt(fySummary.totals.pending)}
              </td>
              <td className="msr-num">{fmt(closingBalance)}</td>
            </tr>
          </tfoot>
        </table>

        <footer className="ebt-print-foot">
          <span>Pending = Transfer out + Journal (GST) − Transfer in − Receipt</span>
          <span>Balance = Opening + Transfer out + Journal (GST) − Transfer in − Receipt</span>
        </footer>
      </section>
    </>
  );
}
